import { useState } from 'react'
import {
  Home, Truck, Map, Radio, LocateFixed, RefreshCw,
  Building2, Search, PanelLeftClose, PanelLeftOpen,
  LayoutDashboard, Gauge, GanttChartSquare, Route,
  Shield, BellRing, Drill, Layers, Wrench, LogOut,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'

// Estrutura de menu espelhando a plataforma Volis. Itens com `id` são funcionais neste mock;
// os demais são da plataforma completa e ficam marcados como "em breve".
const GROUPS = [
  {
    titleKey: 'nav:menu.groups.operation',
    items: [
      { id: 'equipamentos', labelKey: 'nav:menu.items.equipamentos', icon: Truck },
      { id: 'controlador',  labelKey: 'nav:menu.items.controlador',  icon: Map },
      { id: 'eventos',      labelKey: 'nav:menu.items.eventos',      icon: Radio },
      { id: 'tracking',     labelKey: 'nav:menu.items.tracking',     icon: LocateFixed },
      { id: 'ciclos',       labelKey: 'nav:menu.items.ciclos',       icon: RefreshCw },
      { id: 'furos',        labelKey: 'nav:menu.items.furos',        icon: Drill },
    ],
  },
  {
    titleKey: 'nav:menu.groups.production',
    items: [
      { id: 'dashboard',     labelKey: 'nav:menu.items.dashboard',     icon: LayoutDashboard },
      { id: 'produtividade', labelKey: 'nav:menu.items.produtividade', icon: Gauge },
      { id: 'timeline',      labelKey: 'nav:menu.items.timeline',      icon: GanttChartSquare },
      { id: 'despacho',      labelKey: 'nav:menu.items.despacho',      icon: Route },
    ],
  },
  {
    titleKey: 'nav:menu.groups.administration',
    items: [
      // Abre a casca de Administração (abas Frota/Minas/Equipe/Zonas/Produção). Ativo em qualquer aba admin-*.
      { id: 'admin-frota', labelKey: 'nav:menu.items.admin-frota', icon: Building2, activePrefix: 'admin-' },
      // Geofences delimita zonas de mineração (cadastro de áreas), não é segurança.
      { id: 'geofences',   labelKey: 'nav:menu.items.geofences',   icon: Shield },
      // Maps: aerolevantamentos (GeoTIFF) processados em tiles e sobrepostos ao Mapa.
      { id: 'mapas',       labelKey: 'nav:menu.items.mapas',       icon: Layers },
      // Debug: diagnóstico e limpeza por VMC (ambiente de desenvolvimento).
      { id: 'admin-debug', labelKey: 'nav:menu.items.admin-debug', icon: Wrench, role: 'developer' },
    ],
  },
]

// `role` num item é o perfil mínimo para ele APARECER. É cosmético: quem recusa de
// verdade é o backend, em toda rota. Esconder serve para não oferecer um botão que vai
// devolver 403.
function visibleGroups(can) {
  return GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => !i.role || can(i.role)) }))
    .filter(g => g.items.length > 0) // grupo vazio deixaria um título com borda e nada dentro
}

function initials(user) {
  const src = (user?.name || user?.email || '').trim()
  if (!src) return 'CS'
  const parts = src.split(/[\s@.]+/).filter(Boolean)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'CS'
}

function Item({ item, open, active, onClick, badge = 0 }) {
  const { t } = useTranslation()
  const Icon = item.icon
  if (!open) {
    return (
      <button
        onClick={onClick}
        title={item.label}
        disabled={item.soon}
        className={`relative flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${
          active ? 'bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 shadow-sm' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
        } ${item.soon ? 'opacity-40 cursor-default' : ''}`}
      >
        <Icon size={18} className={active ? 'text-brand-600' : 'text-slate-400 dark:text-slate-500'} />
        {badge > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-status-down" />}
      </button>
    )
  }
  return (
    <button
      onClick={onClick}
      disabled={item.soon}
      className={`flex items-center gap-2.5 w-full min-h-[40px] px-2.5 py-2 rounded-lg transition-colors ${
        active ? 'bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 shadow-sm' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
      } ${item.soon ? 'opacity-50 cursor-default' : ''}`}
    >
      <Icon size={18} className={active ? 'text-brand-600' : 'text-slate-400 dark:text-slate-500'} />
      <span className={`text-sm ${active ? 'text-slate-800 dark:text-slate-100 font-semibold' : 'text-slate-600 dark:text-slate-300 font-medium'}`}>
        {item.label}
      </span>
      {badge > 0 && (
        <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-status-down text-white tabular-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {item.soon && <span className="ml-auto text-[9px] uppercase tracking-wide text-slate-300 dark:text-slate-600">{t('common:states.soon')}</span>}
    </button>
  )
}

export default function Sidebar({ currentView, onViewChange }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)
  const { connected, alarms } = useWS()
  const { dark } = useTheme()
  const { user, can, logout } = useAuth()
  const [logoutError, setLogoutError] = useState(null)
  // O logout só limpa o estado local quando o servidor confirma (ver AuthContext): se a
  // rede cair, a sessão continua viva e o operador precisa saber disso.
  const doLogout = async () => {
    try { setLogoutError(null); await logout() } catch { setLogoutError(t('auth:errors.logout')) }
  }
  const unackedAlarms = (alarms || []).filter(a => !a.acknowledged).length

  return (
    <aside className={`h-full flex flex-col gap-4 bg-offwhite dark:bg-slate-950 border-r border-slate-200 dark:border-slate-700 px-3 py-4 transition-all duration-300 shrink-0 ${open ? 'w-64' : 'w-[72px]'}`}>
      {/* Logo + busca + toggle */}
      <div className="flex flex-col items-center gap-3">
        <img
          src={open ? (dark ? '/carcosa-logo-dark.svg' : '/carcosa-logo.svg') : '/carcosa-mark.svg'}
          alt="Carcosa Secrets"
          className={open ? 'h-5 w-auto self-start ml-1' : 'h-6 w-auto'}
        />
        <div className="w-full flex items-center gap-2">
          {open && (
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                placeholder={t('common:actions.search')}
                className="w-full h-9 pl-8 pr-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </div>
          )}
          <button
            onClick={() => setOpen(o => !o)}
            title={open ? t('nav:menu.collapse') : t('nav:menu.expand')}
            className="h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"
          >
            {open ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
          </button>
        </div>
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin -mx-1 px-1">
        <Item item={{ id: 'inicio', label: t('nav:menu.items.inicio'), icon: Home }} open={open} active={currentView === 'inicio'} onClick={() => onViewChange('inicio')} />
        <Item item={{ id: 'alarmes', label: t('nav:menu.items.alarmes'), icon: BellRing }} open={open} active={currentView === 'alarmes'} onClick={() => onViewChange('alarmes')} badge={unackedAlarms} />
        <div className="border-t border-slate-200 dark:border-slate-700 my-3" />
        {visibleGroups(can).map(group => (
          <div key={group.titleKey} className="pb-3 mb-3 border-b border-slate-200 dark:border-slate-700 last:border-0 last:mb-0">
            {open && (
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {t(group.titleKey)}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item, i) => (
                <Item
                  key={item.id ?? `${group.titleKey}-${i}`}
                  item={{ ...item, label: t(item.labelKey) }}
                  open={open}
                  active={item.activePrefix ? (currentView.startsWith(item.activePrefix) && currentView !== 'admin-debug') : item.id === currentView}
                  onClick={() => item.id && onViewChange(item.id)}
                  badge={item.id === 'alarmes' ? unackedAlarms : 0}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Perfil */}
      <div className={`flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 ${open ? '' : 'justify-center'}`}>
        {user?.picture ? (
          <img src={user.picture} alt="" referrerPolicy="no-referrer"
               className="w-9 h-9 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center text-xs font-semibold shrink-0">
            {initials(user)}
          </div>
        )}
        {open && (
          <>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">
                {user?.name || user?.email || t('nav:menu.profile_name')}
              </p>
              {/* Rótulo CURTO: o longo ("Produto (somente leitura)") não cabe na
                  sidebar e saía cortado no meio do parêntese. O completo fica no title. */}
              <p className="text-xs text-slate-400 dark:text-slate-500 truncate flex items-center gap-1.5"
                 title={user ? t(`auth:roles.${user.role}`) : undefined}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-status-op' : 'bg-slate-300'}`} />
                {user ? t(`auth:roles_short.${user.role}`) : (connected ? t('common:states.online') : t('common:states.offline'))}
              </p>
            </div>
            <button
              type="button"
              onClick={doLogout}
              title={t('auth:sign_out')}
              aria-label={t('auth:sign_out')}
              className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600
                         dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <LogOut size={16} />
            </button>
          </>
        )}
      </div>
      {logoutError && open && (
        <p className="px-1 text-[11px] text-status-down">{logoutError}</p>
      )}
    </aside>
  )
}
